import { memo, forwardRef } from 'react';
import ErrorBoundary from '../../ErrorBoundary/ErrorBoundary';
import ChartPanel from './ChartPanel';
import SegmentedToggle from './SegmentedToggle';
import MetricMultiSelect from './MetricMultiSelect';
import InfoTip from '../../common/InfoTip';
import styles from './RatioChartCard.module.css';

const RATIO_MODE_OPTIONS = [
  { value: 'ratio', label: 'Ratio' },
  { value: 'yield', label: 'Yield' },
];

const VALIDITY_INFO_TEXT = {
  ratio: 'Gaps mark non-positive fundamentals or multiples outside the valuation range.',
  yield: 'Yield stays continuous through non-positive fundamentals by default. Tick "Exclude loss periods" to gap them the same way Ratio mode always does.',
};

const RatioChartCard = forwardRef(({
  highcharts, chartOptions, ratioMode = 'ratio', onRatioModeChange = () => {},
  excludeLossPeriods = false, onExcludeLossPeriodsChange = () => {},
  callback, tickedMetrics = [], onTickedMetricsChange = () => {}, embedded = false,
}, ref) => {
  // When `embedded`, a CollapsibleCard owns the outer card chrome + title, so
  // skip the <section> wrapper and the <h2> and left-align the controls row.
  const inner = (
    <>
      <div className={`${styles.header}${embedded ? ` ${styles.headerEmbedded}` : ''}`}>
        {!embedded && <h2 className={styles.title}>Ratio / Yield</h2>}
        <div className={styles.headerControls}>
          <MetricMultiSelect selected={tickedMetrics} onChange={onTickedMetricsChange} />
          <SegmentedToggle
            options={RATIO_MODE_OPTIONS}
            value={ratioMode}
            onChange={onRatioModeChange}
            ariaLabel="Ratio chart mode"
          />
          {ratioMode === 'yield' && (
            <label className={styles.excludeLossToggle}>
              <input
                type="checkbox"
                checked={excludeLossPeriods}
                onChange={onExcludeLossPeriodsChange}
              />
              Exclude loss periods
            </label>
          )}
          <InfoTip text={VALIDITY_INFO_TEXT[ratioMode]} />
        </div>
      </div>
      {/* Own boundary, same reasoning as the main chart's — a fault here
          shouldn't blank the main chart card or vice versa. */}
      <ErrorBoundary message="Chart couldn't render.">
        <ChartPanel ref={ref} highcharts={highcharts} chartOptions={chartOptions} callback={callback} />
      </ErrorBoundary>
    </>
  );

  return embedded ? inner : <section className={styles.card}>{inner}</section>;
});

export default memo(RatioChartCard);
